import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Loading, Card, EmptyState } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Archive, ShieldAlert, Lock, Unlock, CalendarClock, FileArchive, Plus, Gavel } from "lucide-react";

const inputCls = "h-10 rounded-lg bg-white border border-brand-line px-3 text-sm text-brand-ink outline-none focus:border-brand-civic";
const selectCls = "h-10 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic";
const primaryBtn = "h-10 px-4 rounded-lg bg-brand-navy text-white font-semibold hover:bg-brand-navy-700 inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50";
const ghostBtn = "h-9 px-3 rounded-lg border border-brand-line text-sm font-semibold text-brand-navy hover:bg-brand-bg inline-flex items-center gap-1.5";

const STATUS_PILL = {
  retention_running: "bg-brand-civic-50 text-brand-civic border-brand-civic/30",
  retained_extended: "bg-brand-teal-50 text-brand-teal border-brand-teal/30",
  eligible_for_review: "bg-brand-gold/15 text-[#7A5B0B] border-brand-gold/30",
  on_hold: "bg-slate-200 text-slate-700 border-slate-300",
  approved_for_disposition: "bg-brand-navy/10 text-brand-navy border-brand-navy/20",
};
const STATUS_LABEL = {
  retention_running: "Retention Running", retained_extended: "Retained / Extended",
  eligible_for_review: "Eligible for Review", on_hold: "On Hold",
  approved_for_disposition: "Approved for Disposition",
};

function StatusBadge({ status }) {
  return (
    <span data-testid={`retention-status-${status}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_PILL[status] || "bg-slate-100 text-slate-500 border-slate-300"}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

/* ============================ DATA EXPORT ============================ */
const EXPORT_TYPES = [
  { key: "insights", label: "Insights", sensitive: false },
  { key: "meetings", label: "Meetings", sensitive: false },
  { key: "attendance", label: "Attendance", sensitive: false },
  { key: "considerations", label: "Considerations / Decisions", sensitive: false },
  { key: "board-votes", label: "Board Votes", sensitive: true },
  { key: "audit", label: "Audit Events", sensitive: true },
];

async function downloadExport(type, format, params) {
  const qs = new URLSearchParams({ format, ...params });
  Object.keys(params).forEach((k) => { if (!params[k]) qs.delete(k); });
  const res = await api.get(`/exports/${type}?${qs.toString()}`, { responseType: "blob" });
  const dispo = res.headers["content-disposition"] || "";
  const m = dispo.match(/filename="?([^"]+)"?/);
  const filename = m ? m[1] : `govflowstate_${type}.${format === "json" ? "json" : "csv"}`;
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

export function DataExportTab() {
  const [type, setType] = useState("insights");
  const [format, setFormat] = useState("csv");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const active = EXPORT_TYPES.find((t) => t.key === type);

  const run = async () => {
    setBusy(true);
    try {
      await downloadExport(type, format, { date_from: from, date_to: to });
      toast.success(`Export generated (${format.toUpperCase()})`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export could not be created.");
    } finally { setBusy(false); }
  };

  const manifest = async () => {
    setBusy(true);
    try {
      const res = await api.get("/exports/organization/manifest");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "govflowstate_organization_manifest.json";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Organization export manifest downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Manifest unavailable");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="data-export-tab">
      <Card>
        <div className="flex items-center gap-2 mb-1"><Download className="h-4 w-4 text-brand-civic" /><h3 className="font-display font-semibold text-brand-ink">Data Export</h3></div>
        <p className="text-sm text-slate-500 mb-4">Export your organization's records in common machine-readable formats. Exports respect your access — you only receive records you are authorized to see.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="text-xs font-semibold text-slate-500">What to export</span>
            <select data-testid="export-type-select" value={type} onChange={(e) => setType(e.target.value)} className={`${selectCls} w-full mt-1`}>
              {EXPORT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}{t.sensitive ? " (restricted)" : ""}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs font-semibold text-slate-500">Format</span>
            <div className="mt-1 flex gap-2">
              {["csv", "json"].map((f) => (
                <button key={f} data-testid={`export-format-${f}`} onClick={() => setFormat(f)}
                  className={`h-10 flex-1 rounded-lg border text-sm font-semibold uppercase transition-colors ${format === f ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-brand-navy border-brand-line hover:bg-brand-bg"}`}>{f}</button>
              ))}
            </div>
          </label>
          <label className="block"><span className="text-xs font-semibold text-slate-500">From date</span>
            <input type="date" data-testid="export-date-from" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputCls} w-full mt-1`} />
          </label>
          <label className="block"><span className="text-xs font-semibold text-slate-500">To date</span>
            <input type="date" data-testid="export-date-to" value={to} onChange={(e) => setTo(e.target.value)} className={`${inputCls} w-full mt-1`} />
          </label>
        </div>
        {active?.sensitive && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-brand-gold/10 border border-brand-gold/30 px-3 py-2 text-[12px] text-[#7A5B0B]">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This is a restricted dataset and requires elevated authorization.
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button data-testid="run-export-btn" onClick={run} disabled={busy} className={primaryBtn}>
            <Download className="h-4 w-4" /> Export {active?.label}
          </button>
          <span className="text-xs text-slate-400">CSV opens in spreadsheets; JSON preserves full structure.</span>
        </div>
      </Card>
      <Card>
        <div className="flex items-center gap-2 mb-1"><FileArchive className="h-4 w-4 text-brand-civic" /><h3 className="font-display font-semibold text-brand-ink">Organization Data Export</h3></div>
        <p className="text-sm text-slate-500 mb-3">Your records are portable. Download a manifest describing every exportable record type and count — demonstrating that your data is not locked into GovFlowState.</p>
        <button data-testid="org-manifest-btn" onClick={manifest} disabled={busy} className={ghostBtn}><Archive className="h-4 w-4" /> Download Export Manifest</button>
      </Card>
    </div>
  );
}

/* ============================ RECORDS MANAGEMENT ============================ */
function ReasonDialog({ open, onClose, title, description, onSubmit, extra, submitLabel = "Confirm", testid }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required."); return; }
    setBusy(true);
    try { await onSubmit(reason.trim()); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{description}</DialogDescription></DialogHeader>
        {extra}
        <label className="block"><span className="text-xs font-semibold text-slate-500">Reason (required)</span>
          <textarea data-testid={`${testid}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            className="mt-1 w-full rounded-lg border border-brand-line px-3 py-2 text-sm outline-none focus:border-brand-civic" /></label>
        <DialogFooter>
          <button onClick={onClose} className={ghostBtn}>Cancel</button>
          <button data-testid={`${testid}-submit`} onClick={submit} disabled={busy} className={primaryBtn}>{submitLabel}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RecordsTab() {
  const [dash, setDash] = useState(null);
  const load = () => api.get("/records/dashboard").then((r) => setDash(r.data)).catch(() => setDash(false));
  useEffect(() => { load(); }, []);
  if (dash === null) return <Loading />;
  if (dash === false) return <EmptyState icon={ShieldAlert} title="Records Management unavailable" description="You need the Records Management capability." />;
  return (
    <div className="space-y-4" data-testid="records-management">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[["On Hold", dash.on_hold, "on-hold"], ["Eligible for Review", dash.eligible_for_review, "eligible"],
          ["Due in 30 Days", dash.due_30_days, "due30"], ["Due in 90 Days", dash.due_90_days, "due90"],
          ["Policies", dash.policies, "policies"], ["Classifications", dash.classifications, "classifications"]].map(([l, v, id]) => (
          <Card key={id} data-testid={`records-stat-${id}`} className="!p-3">
            <div className="text-2xl font-display font-bold text-brand-navy">{v ?? 0}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">{l}</div>
          </Card>
        ))}
      </div>
      <Tabs defaultValue="retentions">
        <TabsList className="bg-white border border-brand-line flex-wrap h-auto">
          {[["retentions", "Retentions"], ["upcoming", "Upcoming Dispositions"], ["policies", "Policies"], ["classifications", "Classifications"], ["holds", "Holds"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`records-subtab-${v}`} className="data-[state=active]:bg-brand-navy data-[state=active]:text-white text-slate-500">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="retentions" className="mt-3"><RetentionsList onChange={load} /></TabsContent>
        <TabsContent value="upcoming" className="mt-3"><UpcomingList /></TabsContent>
        <TabsContent value="policies" className="mt-3"><PoliciesList onChange={load} /></TabsContent>
        <TabsContent value="classifications" className="mt-3"><ClassificationsList onChange={load} /></TabsContent>
        <TabsContent value="holds" className="mt-3"><HoldsList onChange={load} /></TabsContent>
      </Tabs>
    </div>
  );
}

function RetentionRow({ r, onChange }) {
  const [dlg, setDlg] = useState(null);
  const doRetain = async (reason) => {
    await api.post(`/records/retentions/${r.retention_id}/retain`, { extend_period: 5, extend_unit: "years", reason });
    toast.success("Record retained / extended"); onChange();
  };
  const doApprove = async (reason) => {
    await api.post(`/records/retentions/${r.retention_id}/approve-disposition`, { reason });
    toast.success("Approved for disposition (record preserved)"); onChange();
  };
  const doHold = async (reason) => {
    await api.post("/records/holds", { record_type: r.record_type, record_id: r.record_id, reason, hold_label: "Records Hold" });
    toast.success("Record Hold placed"); onChange();
  };
  return (
    <div className="rounded-lg border border-brand-line bg-white px-3 py-2.5" data-testid={`retention-${r.retention_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-brand-ink truncate">{r.record_label || r.record_id}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            <span className="uppercase">{r.record_type}</span> · {r.classification_name} · Policy: {r.policy_name} v{r.policy_version} ({r.retention_period} {r.retention_unit})
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Retention start <strong className="text-brand-ink">{fmtDate(r.retention_start)}</strong> · Disposition review <strong className="text-brand-ink">{fmtDate(r.eligible_disposition_date)}</strong>
          </div>
          {r.disposition_blocked_by_hold && <div className="text-xs font-semibold text-slate-700 mt-1 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Disposition blocked by {r.hold?.label || "hold"}</div>}
        </div>
        <StatusBadge status={r.derived_status} />
        <div className="flex items-center gap-1.5">
          <button data-testid={`retain-${r.retention_id}`} onClick={() => setDlg("retain")} className={ghostBtn}><CalendarClock className="h-3.5 w-3.5" /> Retain</button>
          {!r.disposition_blocked_by_hold && <button data-testid={`hold-${r.retention_id}`} onClick={() => setDlg("hold")} className={ghostBtn}><Lock className="h-3.5 w-3.5" /> Hold</button>}
          {!r.disposition_blocked_by_hold && r.derived_status !== "approved_for_disposition" && <button data-testid={`approve-${r.retention_id}`} onClick={() => setDlg("approve")} className={ghostBtn}><Gavel className="h-3.5 w-3.5" /> Approve Disposition</button>}
        </div>
      </div>
      <ReasonDialog open={dlg === "retain"} onClose={() => setDlg(null)} testid="retain-dialog"
        title="Retain / Extend Record" description="Extend retention by 5 years. Previous schedule is preserved in history." onSubmit={doRetain} submitLabel="Retain 5 Years" />
      <ReasonDialog open={dlg === "hold"} onClose={() => setDlg(null)} testid="hold-dialog"
        title="Place Record Hold" description="A hold suspends disposition without changing the original retention date." onSubmit={doHold} submitLabel="Place Hold" />
      <ReasonDialog open={dlg === "approve"} onClose={() => setDlg(null)} testid="approve-dialog"
        title="Approve for Disposition" description="Records approval for future disposition. This does NOT delete the record." onSubmit={doApprove} submitLabel="Approve" />
    </div>
  );
}

function RetentionsList({ onChange }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/records/retentions").then((r) => setRows(r.data.retentions)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); onChange && onChange(); };
  if (rows === null) return <Loading />;
  if (!rows.length) return <EmptyState icon={CalendarClock} title="No records under retention" description="Assign a retention classification to a meeting or insight to begin." />;
  return <div className="space-y-2" data-testid="retentions-list">{rows.map((r) => <RetentionRow key={r.retention_id} r={r} onChange={refresh} />)}</div>;
}

function UpcomingList() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/records/upcoming?days=90").then((r) => setData(r.data)).catch(() => setData({ records: [] })); }, []);
  if (data === null) return <Loading />;
  if (!data.records.length) return <EmptyState icon={CalendarClock} title="Nothing due within 90 days" description="No records are approaching their disposition review date." />;
  return (
    <div className="space-y-2" data-testid="upcoming-list">
      {data.records.map((r) => (
        <div key={r.retention_id} className="flex items-center justify-between rounded-lg border border-brand-line bg-white px-3 py-2 text-sm">
          <span className="text-brand-ink truncate">{r.record_label} <span className="text-xs text-slate-400">· {r.classification_name}</span></span>
          <span className="flex items-center gap-2"><span className="text-xs text-slate-500">{fmtDate(r.eligible_disposition_date)}</span><StatusBadge status={r.derived_status} /></span>
        </div>
      ))}
    </div>
  );
}

function PoliciesList({ onChange }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ family_name: "", retention_period: 7, retention_unit: "years", trigger_event: "meeting_completion" });
  const load = () => api.get("/records/policies").then((r) => setRows(r.data.policies)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.family_name.trim()) { toast.error("Name is required."); return; }
    try { await api.post("/records/policies", { ...form, retention_period: Number(form.retention_period) }); toast.success("Policy created"); setForm({ ...form, family_name: "" }); load(); onChange && onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (rows === null) return <Loading />;
  return (
    <div className="space-y-3" data-testid="policies-list">
      <Card className="!p-3">
        <div className="text-sm font-semibold text-brand-ink mb-2 flex items-center gap-1.5"><Plus className="h-4 w-4" /> New Retention Policy</div>
        <div className="grid sm:grid-cols-4 gap-2">
          <input data-testid="policy-name" placeholder="Policy name" value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} className={inputCls} />
          <input data-testid="policy-period" type="number" min="1" value={form.retention_period} onChange={(e) => setForm({ ...form, retention_period: e.target.value })} className={inputCls} />
          <select data-testid="policy-unit" value={form.retention_unit} onChange={(e) => setForm({ ...form, retention_unit: e.target.value })} className={selectCls}>{["years", "months", "days"].map((u) => <option key={u}>{u}</option>)}</select>
          <select data-testid="policy-trigger" value={form.trigger_event} onChange={(e) => setForm({ ...form, trigger_event: e.target.value })} className={selectCls}>{["record_creation", "insight_finalization", "insight_closure", "meeting_completion", "publication"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</select>
        </div>
        <button data-testid="policy-create" onClick={create} className={`${primaryBtn} mt-2`}>Create Policy</button>
        <p className="text-[11px] text-slate-400 mt-1.5">Each organization configures its own legally-appropriate retention schedule. GovFlowState provides the controls, not the legal determination.</p>
      </Card>
      {rows.map((p) => (
        <div key={p.policy_id} className="rounded-lg border border-brand-line bg-white px-3 py-2 text-sm" data-testid={`policy-${p.family_id}`}>
          <span className="font-semibold text-brand-ink">{p.family_name}</span> <span className="text-xs text-slate-400">v{p.version}</span>
          <span className="text-slate-500"> — {p.retention_period} {p.retention_unit} after {p.trigger_event.replace(/_/g, " ")}{p.review_required ? " · review required" : ""}</span>
        </div>
      ))}
    </div>
  );
}

function ClassificationsList({ onChange }) {
  const [rows, setRows] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [form, setForm] = useState({ name: "", policy_family_id: "" });
  const load = () => api.get("/records/classifications").then((r) => setRows(r.data.classifications)).catch(() => setRows([]));
  useEffect(() => { load(); api.get("/records/policies").then((r) => setPolicies(r.data.policies)).catch(() => {}); }, []);
  const create = async () => {
    if (!form.name.trim() || !form.policy_family_id) { toast.error("Name and policy are required."); return; }
    try { await api.post("/records/classifications", form); toast.success("Classification created"); setForm({ name: "", policy_family_id: "" }); load(); onChange && onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (rows === null) return <Loading />;
  return (
    <div className="space-y-3" data-testid="classifications-list">
      <Card className="!p-3">
        <div className="text-sm font-semibold text-brand-ink mb-2 flex items-center gap-1.5"><Plus className="h-4 w-4" /> New Classification</div>
        <div className="grid sm:grid-cols-3 gap-2">
          <input data-testid="classification-name" placeholder="Classification name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <select data-testid="classification-policy" value={form.policy_family_id} onChange={(e) => setForm({ ...form, policy_family_id: e.target.value })} className={`${selectCls} sm:col-span-2`}>
            <option value="">Select a retention policy…</option>
            {policies.map((p) => <option key={p.family_id} value={p.family_id}>{p.family_name} v{p.version}</option>)}
          </select>
        </div>
        <button data-testid="classification-create" onClick={create} className={`${primaryBtn} mt-2`}>Create Classification</button>
      </Card>
      {rows.map((c) => (
        <div key={c.classification_id} className="rounded-lg border border-brand-line bg-white px-3 py-2 text-sm" data-testid={`classification-${c.classification_id}`}>
          <span className="font-semibold text-brand-ink">{c.name}</span>
          <span className="text-slate-500"> — {c.current_policy ? `${c.current_policy.retention_period} ${c.current_policy.retention_unit} after ${c.current_policy.trigger_event.replace(/_/g, " ")}` : "no current policy"}</span>
        </div>
      ))}
    </div>
  );
}

function HoldsList({ onChange }) {
  const [rows, setRows] = useState(null);
  const [rel, setRel] = useState(null);
  const load = () => api.get("/records/holds").then((r) => setRows(r.data.holds)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const doRelease = async (reason) => { await api.post(`/records/holds/${rel.hold_id}/release`, { reason }); toast.success("Hold released"); load(); onChange && onChange(); };
  if (rows === null) return <Loading />;
  if (!rows.length) return <EmptyState icon={Unlock} title="No holds on record" description="Place a hold from the Retentions tab to prevent disposition." />;
  return (
    <div className="space-y-2" data-testid="holds-list">
      {rows.map((h) => (
        <div key={h.hold_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-brand-line bg-white px-3 py-2 text-sm" data-testid={`hold-row-${h.hold_id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-brand-ink truncate"><strong>{h.hold_label}</strong> · {h.record_label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{h.reason} · placed by {h.placed_by_name} {fmtDate(h.placed_at)}{!h.active && ` · released ${fmtDate(h.released_at)}`}</div>
          </div>
          {h.active
            ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700"><Lock className="h-3 w-3" /> Active</span>
            : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400"><Unlock className="h-3 w-3" /> Released</span>}
          {h.active && <button data-testid={`release-${h.hold_id}`} onClick={() => setRel(h)} className={ghostBtn}><Unlock className="h-3.5 w-3.5" /> Release</button>}
        </div>
      ))}
      <ReasonDialog open={!!rel} onClose={() => setRel(null)} testid="release-dialog"
        title="Release Record Hold" description="Releasing preserves the hold history. The original retention date is unchanged." onSubmit={doRelease} submitLabel="Release Hold" />
    </div>
  );
}
